/**
 * Academy assistant client (B8 · §I).
 *
 * Two very different surfaces:
 *
 * - **Public** (`ask`): used by people WITHOUT an account, so it sends no
 *   session headers or active-account cookies. Identity is an opaque session id
 *   stored in `sessionStorage` — it isn't a credential, it just groups a visit's
 *   turns so a cap can be put on them.
 * - **Administration**: FAQ, unanswered questions, settings and deflection, with
 *   the usual cookie-BFF auth.
 */
import { createSafeQuery } from "@/lib/api";
import { createMutation, useQueryClient } from "@tanstack/solid-query";
import { API_ORIGIN, apiJson, readApiJson } from "@/lib/rest";
import { generateId } from "@fleet/utils";

const BASE = "/api";
const ANON_KEY = "academy-bot.session";

/** Anonymous session id, stable within the tab. */
export function anonSessionId(): string {
  if (typeof sessionStorage === "undefined") {
    return "";
  }
  const existing = sessionStorage.getItem(ANON_KEY);
  if (existing) {
    return existing;
  }
  const fresh = generateId();
  sessionStorage.setItem(ANON_KEY, fresh);
  return fresh;
}

// ─── Public ─────────────────────────────────────────────────────────────────

export interface BotCitation {
  id: string;
  title: string;
  source: string;
}

export interface AskResponse {
  conversationId: string;
  answer: string;
  citations: BotCitation[];
  confidence: number;
  escalated: boolean;
  escalationReason: string | null;
  whatsappUrl: string | null;
  leadCreated: boolean;
}

export interface AskInput {
  orgSlug: string;
  message: string;
  conversationId?: string;
  contactName?: string;
  contactPhone?: string;
}

export const useAskAcademyBot = () =>
  createMutation(() => ({
    mutationFn: async (input: AskInput) => {
      const response = await fetch(`${API_ORIGIN}${BASE}/academy-bot/${input.orgSlug}/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: input.message,
          anonSessionId: anonSessionId(),
          conversationId: input.conversationId,
          contactName: input.contactName,
          contactPhone: input.contactPhone,
        }),
      });
      return readApiJson<AskResponse>(response);
    },
  }));

// ─── Administration ────────────────────────────────────────────────────────

export interface KbEntry {
  id: string;
  question: string;
  answer: string;
  category: string;
  locale: string;
  source: string;
  helpSlug: string | null;
  position: number;
  published: boolean;
  updatedAt: string;
}

export interface UnansweredQuestion {
  id: string;
  question: string;
  hits: number;
  lastSeenAt: string;
  resolved: boolean;
}

export interface DeflectionStats {
  period: string;
  conversations: number;
  escalated: number;
  anonymous: number;
  deflectionRate: number;
  unansweredOpen: number;
}

export interface BotSettings {
  org: string;
  whatsappPhone: string | null;
  greeting: string | null;
  escalationThreshold: number;
  anonTurnCap: number;
}

const admin = <T>(path: string, init?: RequestInit) =>
  apiJson<T>(`${BASE}/academy-bot${path}`, init);

const keys = {
  entries: (org: string) => ["academy-bot", "entries", org] as const,
  unanswered: (org: string) => ["academy-bot", "unanswered", org] as const,
  deflection: (org: string) => ["academy-bot", "deflection", org] as const,
  settings: (org: string) => ["academy-bot", "settings", org] as const,
};

export const useKbEntries = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: keys.entries(org() ?? ""),
    queryFn: () => admin<KbEntry[]>(`/entries?org=${encodeURIComponent(org() ?? "")}`),
    get enabled() {
      return Boolean(org());
    },
    retry: false,
  }));

export const useUnanswered = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: keys.unanswered(org() ?? ""),
    queryFn: () =>
      admin<UnansweredQuestion[]>(`/unanswered?org=${encodeURIComponent(org() ?? "")}`),
    get enabled() {
      return Boolean(org());
    },
    retry: false,
  }));

export const useDeflection = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: keys.deflection(org() ?? ""),
    queryFn: () => admin<DeflectionStats>(`/deflection?org=${encodeURIComponent(org() ?? "")}`),
    get enabled() {
      return Boolean(org());
    },
    retry: false,
  }));

export const useBotSettings = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: keys.settings(org() ?? ""),
    queryFn: () => admin<BotSettings>(`/settings?org=${encodeURIComponent(org() ?? "")}`),
    get enabled() {
      return Boolean(org());
    },
    retry: false,
  }));

export const useUpsertKbEntry = (org: () => string | undefined) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (entry: Partial<KbEntry> & { question: string; answer: string }) =>
      admin<{ id: string }>("/entries", {
        method: "POST",
        body: JSON.stringify({ ...entry, org: org() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.entries(org() ?? "") });
    },
  }));
};

export const useDeleteKbEntry = (org: () => string | undefined) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (id: string) =>
      admin<{ ok: boolean }>(`/entries/${id}?org=${encodeURIComponent(org() ?? "")}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.entries(org() ?? "") });
    },
  }));
};

/**
 * Converts an unanswered question into a FAQ entry. It's the loop that
 * makes the chatbot improve: every failure turns into a line in the KB.
 */
export const useResolveUnanswered = (org: () => string | undefined) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { id: string; entryId?: string }) =>
      admin<{ ok: boolean }>(`/unanswered/${input.id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ org: org(), entryId: input.entryId }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.unanswered(org() ?? "") });
      void qc.invalidateQueries({ queryKey: keys.deflection(org() ?? "") });
    },
  }));
};

export const useSaveBotSettings = (org: () => string | undefined) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (settings: Omit<BotSettings, "org">) =>
      admin<{ ok: boolean }>("/settings", {
        method: "POST",
        body: JSON.stringify({ ...settings, org: org() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.settings(org() ?? "") });
    },
  }));
};
